import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Kit Tabs — lightweight controlled tab bar. Radix has no react-tabs
 * package installed in this repo (and this worktree cannot run npm
 * install), so this composes the same pattern shadcn's Tabs uses
 * (context + role=tablist/tab) directly against our Tailwind/token
 * classes instead. Hover/active state is CSS (`hover:`), never a JS
 * mouseenter/mouseleave DOM-style mutation.
 *
 * Usage:
 *   <Tabs value={tab} onValueChange={setTab}>
 *     <TabsList>
 *       <TabsTrigger value="a">A</TabsTrigger>
 *       <TabsTrigger value="b">B</TabsTrigger>
 *     </TabsList>
 *   </Tabs>
 */
interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}
const TabsContext = React.createContext<TabsContextValue | null>(null);

export interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

export function Tabs({ value, onValueChange, className, children }: TabsProps) {
  const ctx = React.useMemo(() => ({ value, onValueChange }), [value, onValueChange]);
  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export const TabsList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      role="tablist"
      className={cn('flex gap-0.5 border-b border-border', className)}
      {...props}
    >
      {children}
    </div>
  )
);
TabsList.displayName = 'TabsList';

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

export const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ value, className, children, onKeyDown, ...props }, ref) => {
    const ctx = React.useContext(TabsContext);
    if (!ctx) throw new Error('TabsTrigger must be rendered inside <Tabs>');
    const active = ctx.value === value;
    // Roving tabIndex (only the active tab is Tab-reachable) means the
    // WAI-ARIA tablist pattern REQUIRES arrow-key navigation between tabs,
    // or a keyboard user can never reach the other two — this is what
    // makes "full tab reachability" (docs/DESIGN.md §7) actually true here.
    const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      const list = e.currentTarget.closest('[role="tablist"]');
      if (!list) return;
      const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
      const i = tabs.indexOf(e.currentTarget);
      let next = -1;
      if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      if (next === -1) return;
      e.preventDefault();
      tabs[next].focus();
      tabs[next].click();
    };
    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={active}
        tabIndex={active ? 0 : -1}
        onClick={() => ctx.onValueChange(value)}
        onKeyDown={onTabKeyDown}
        className={cn(
          '-mb-px rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors duration-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          active
            ? 'border-primary font-bold text-primary'
            : 'border-transparent text-muted hover:bg-surface-alt hover:text-text',
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
TabsTrigger.displayName = 'TabsTrigger';
